import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Lock, User, Briefcase, Eye, EyeOff } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Detectar contexto baseado no domínio
const getContext = () => {
  const hostname = window.location.hostname;
  if (hostname.startsWith('dashboard.') || hostname.includes('dashboard')) {
    return 'dashboard';
  }
  if (hostname.startsWith('crm.') || hostname.includes('crm')) {
    return 'crm';
  }
  return 'preview';
};

const Login = ({ onLogin }) => {
  const navigate = useNavigate();
  const context = getContext();
  const [isRegister, setIsRegister] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [sendingReset, setSendingReset] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "corretor"
  });
  const [loading, setLoading] = useState(false);
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    document.title = "InstaIMOB CRM";
    
    const checkDark = () => {
      setIsDark(document.documentElement.classList.contains('dark'));
    };
    checkDark();
    
    const observer = new MutationObserver(checkDark);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isRegister ? "/api/auth/register" : "/api/auth/login";
      const payload = isRegister 
        ? formData 
        : { email: formData.email, password: formData.password };
      
      const fullUrl = `${BACKEND_URL}${endpoint}`;
      
      const response = await axios.post(fullUrl, payload);
      
      
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      // Salvar tenant info se existir
      if (response.data.tenant) {
        localStorage.setItem('tenant', JSON.stringify(response.data.tenant));
      }
      
      toast.success(isRegister ? "Conta criada com sucesso!" : "Login realizado com sucesso!");
      onLogin();
    } catch (error) {
      console.error('❌ Erro no login:', error);
      console.error('❌ Error details:', error.response?.data);
      toast.error(error.response?.data?.detail || "Erro ao processar solicitação");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error("Digite seu email");
      return;
    }
    
    setSendingReset(true);
    try {
      // Por enquanto, mostrar mensagem informativa
      // TODO: Implementar endpoint de recuperação de senha
      toast.success("Se este email estiver cadastrado, você receberá as instruções de recuperação.");
      setShowForgotPassword(false);
      setForgotEmail("");
    } catch (error) {
      toast.error("Erro ao processar solicitação");
    } finally {
      setSendingReset(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#2a2a2a' }}>
      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-slate-900/80 rounded-3xl p-8 md:p-12 shadow-2xl">
          {/* Logo InstaIMOB */}
          <div className="flex items-center justify-center mb-8">
            <button
              onClick={() => {
                const token = localStorage.getItem('token');
                if (token) {
                  navigate('/dashboard');
                }
              }}
              className="cursor-pointer hover:opacity-80 transition-opacity"
            >
              <img 
                src="/logo-instaimob-black.png"
                alt="InstaIMOB CRM" 
                className="h-16 w-auto object-contain"
              />
            </button>
          </div>



          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            {isRegister && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Nome
                  </label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 dark:text-slate-500" />
                    <input
                      type="text"
                      data-testid="name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full pl-12 pr-4 py-3 rounded-xl glass-input dark:text-slate-100 focus:outline-none"
                      placeholder="Seu nome completo"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Cargo
                  </label>
                  <div className="relative">
                    <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 dark:text-slate-500" />
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      data-testid="role-select"
                      className="w-full pl-12 pr-4 py-3 rounded-xl glass-input dark:text-slate-100 focus:outline-none appearance-none cursor-pointer"
                      required
                    >
                      <option value="corretor">Corretor de Imóveis</option>
                      <option value="cadastrador">Cadastrador</option>
                      <option value="admin">Administrador</option>
                      <option value="proprietario">Proprietário</option>
                    </select>
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                      <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 text-left">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 dark:text-slate-500" />
                <input
                  type="email"
                  data-testid="email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full pl-12 pr-4 py-3 rounded-xl glass-input dark:text-slate-100 focus:outline-none"
                  placeholder="seu@email.com"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 text-left">
                Senha
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 dark:text-slate-500" />
                <input
                  type={showPassword ? "text" : "password"}
                  data-testid="password-input"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full pl-12 pr-12 py-3 rounded-xl glass-input dark:text-slate-100 focus:outline-none"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {!isRegister && (
                <div className="mt-2 text-right">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-xs text-primary hover:text-primary/80 transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Esqueceu sua senha?
                  </button>
                </div>
              )}
            </div>

            <button
              type="submit"
              data-testid="submit-button"
              disabled={loading}
              className="w-full bg-primary hover:bg-primary/90 text-white rounded-full py-3 font-medium shadow-lg shadow-primary/20 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Processando..." : isRegister ? "Criar Conta" : "Entrar"}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              data-testid="toggle-mode-button"
              onClick={() => setIsRegister(!isRegister)}
              className="text-sm text-slate-600 dark:text-slate-300 hover:text-primary dark:hover:text-primary transition-colors"
            >
              {isRegister ? "Já tem uma conta? Faça login" : "Não tem uma conta? Cadastre-se"}
            </button>
          </div>
        </div>
      </div>
      
      {/* Modal de Recuperação de Senha */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-2">
              Recuperar Senha
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
              Digite seu email para receber as instruções de recuperação de senha.
            </p>
            
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 rounded-xl border border-slate-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="seu@email.com"
                  required
                  data-testid="forgot-email-input"
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotEmail("");
                  }}
                  className="flex-1 py-3 rounded-xl border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={sendingReset}
                  className="flex-1 py-3 rounded-xl bg-primary text-white hover:bg-primary/90 transition-colors disabled:opacity-50"
                  data-testid="send-reset-button"
                >
                  {sendingReset ? "Enviando..." : "Enviar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
